// ============================================================
// videoFonts.js — curated font set for Video Editor text overlays
// ------------------------------------------------------------
// A small, hand-picked set of display / sans / serif / script fonts. Each entry
// carries a public .ttf URL (jsDelivr-hosted Google Fonts) so the SAME file can:
//   • render in the live preview via an injected @font-face (see fontFaceCss), and
//   • be handed to Rendi's FFmpeg as a `fontfile` input for the final render —
//     a drawtext `font=` family name alone won't resolve on Rendi's box, so the
//     overlay payload ships the ttfUrl and the backend downloads it as an input.
//
// All URLs were verified to resolve (HTTP 200) when this set was curated. The
// `default` entry has no ttfUrl — it falls back to Rendi's fontconfig default.
//
// The image text overlay (AutopilotDashboard's compositeTextOntoImage) draws
// from this same set, in bold where the font has one: `variable` marks a
// variable-weight file (bold is on its wght axis), `boldTtfUrl` a separate
// bold file. The Video Editor doesn't read either.
// ============================================================

const GF = 'https://cdn.jsdelivr.net/gh/google/fonts@main';

export const VIDEO_FONTS = [
  { id: 'default',  label: 'Default (Sans)', family: 'var(--font-head)',     ttfUrl: null },
  { id: 'anton',    label: 'Anton',          family: "'Anton'",              ttfUrl: `${GF}/ofl/anton/Anton-Regular.ttf` },
  { id: 'bebas',    label: 'Bebas Neue',     family: "'Bebas Neue'",         ttfUrl: `${GF}/ofl/bebasneue/BebasNeue-Regular.ttf` },
  { id: 'oswald',   label: 'Oswald',         family: "'Oswald'",             ttfUrl: `${GF}/ofl/oswald/Oswald%5Bwght%5D.ttf`, variable: true },
  { id: 'montserrat', label: 'Montserrat',   family: "'Montserrat'",         ttfUrl: `${GF}/ofl/montserrat/Montserrat%5Bwght%5D.ttf`, variable: true },
  { id: 'poppins',  label: 'Poppins',        family: "'Poppins'",            ttfUrl: `${GF}/ofl/poppins/Poppins-Regular.ttf`, boldTtfUrl: `${GF}/ofl/poppins/Poppins-Bold.ttf` },
  { id: 'lato',     label: 'Lato',           family: "'Lato'",               ttfUrl: `${GF}/ofl/lato/Lato-Regular.ttf`, boldTtfUrl: `${GF}/ofl/lato/Lato-Bold.ttf` },
  { id: 'playfair', label: 'Playfair Display', family: "'Playfair Display'", ttfUrl: `${GF}/ofl/playfairdisplay/PlayfairDisplay%5Bwght%5D.ttf`, variable: true },
  { id: 'pacifico', label: 'Pacifico',       family: "'Pacifico'",           ttfUrl: `${GF}/ofl/pacifico/Pacifico-Regular.ttf` },
  { id: 'lobster',  label: 'Lobster',        family: "'Lobster'",            ttfUrl: `${GF}/ofl/lobster/Lobster-Regular.ttf` },
  // Script additions (Sep 13 follow-up), same jsDelivr-Google-Fonts pattern.
  // Only Dancing Script ships a variable weight axis — the other three are
  // single-weight (Regular only) upstream, same as Pacifico/Lobster above.
  { id: 'dancingscript', label: 'Dancing Script', family: "'Dancing Script'", ttfUrl: `${GF}/ofl/dancingscript/DancingScript%5Bwght%5D.ttf`, variable: true },
  { id: 'greatvibes', label: 'Great Vibes',   family: "'Great Vibes'",       ttfUrl: `${GF}/ofl/greatvibes/GreatVibes-Regular.ttf` },
  { id: 'alexbrush',  label: 'Alex Brush',    family: "'Alex Brush'",        ttfUrl: `${GF}/ofl/alexbrush/AlexBrush-Regular.ttf` },
  { id: 'parisienne', label: 'Parisienne',    family: "'Parisienne'",        ttfUrl: `${GF}/ofl/parisienne/Parisienne-Regular.ttf` },
];

export const DEFAULT_FONT_ID = 'default';

const FONT_BY_ID = VIDEO_FONTS.reduce((m, f) => { m[f.id] = f; return m; }, {});

// Resolve a stored font id back to its config (falls back to `default`).
export function resolveFont(id) {
  return FONT_BY_ID[id] || FONT_BY_ID[DEFAULT_FONT_ID];
}

// CSS font-family value for the live preview.
export function fontFamilyFor(id) {
  return resolveFont(id).family;
}

// Build @font-face rules so the preview renders the real fonts. We point at the
// same .ttf the renderer uses, keeping preview == output from one source.
export function fontFaceCss() {
  return VIDEO_FONTS
    .filter((f) => f.ttfUrl)
    .map((f) => `@font-face{font-family:${f.family};src:url('${f.ttfUrl}') format('truetype');font-display:swap;}`)
    .join('\n');
}
